/**
 * Barra superior «Despacho» (GEN-PX-003, spec §3.2).
 *
 * Izquierda: empresa + «Pórtico» y, discreto, el título de la vista activa.
 * Derecha: avatar y nombre del usuario, con un menú (Perfil, Preferencias, Cerrar sesión).
 * «Perfil» (GEN-PX-006) abre la pantalla de perfil propio.
 * Sin buscador (GEN-PX-E08) ni asunto activo (HU de asuntos).
 * Los botones de plegar paneles se conservan: hoy son la única forma de plegarlos.
 */
import { useState, useRef, useEffect } from 'react'
import { PanelLeft, Check, ChevronDown } from 'lucide-react'
import { Avatar } from './ui.jsx'
import { inicialesDe } from './Rail.jsx'

const TEMAS = [
  { id: 'system', label: 'Sistema' },
  { id: 'light',  label: 'Claro' },
  { id: 'dark',   label: 'Oscuro' },
]

function MenuUsuario({ user, theme, setTheme, onLogout, onPerfil }) {
  const [abierto, setAbierto] = useState(false)
  const [prefs, setPrefs] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!abierto) return
    const fuera = e => { if (!ref.current?.contains(e.target)) setAbierto(false) }
    const tecla = e => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', tecla)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', tecla)
    }
  }, [abierto])

  const alternar = () => { setAbierto(o => !o); setPrefs(false) }

  return (
    <div className="dk-barra__usuario" ref={ref}>
      <button
        type="button"
        className="dk-barra__usuario-btn"
        onClick={alternar}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={`Menú de ${user.display_name}`}
      >
        <Avatar iniciales={inicialesDe(user.display_name)} nombre="" tamano={28} />
        <span className="dk-barra__nombre">{user.display_name}</span>
        <ChevronDown size={14} strokeWidth={1.8} aria-hidden="true" />
      </button>
      {abierto && (
        <div className="dk-menu" role="menu" aria-label="Usuario">
          <button type="button" role="menuitem" className="dk-menu__item" onClick={() => { setAbierto(false); onPerfil?.() }}>
            Perfil
          </button>
          <button
            type="button" role="menuitem" className="dk-menu__item"
            aria-expanded={prefs} onClick={() => setPrefs(p => !p)}
          >
            Preferencias
            <ChevronDown size={14} strokeWidth={1.8} aria-hidden="true" className={prefs ? 'dk-menu__giro' : undefined} />
          </button>
          {prefs && (
            <div className="dk-menu__prefs" role="group" aria-label="Tema">
              <span className="dk-menu__etiqueta">Tema</span>
              {TEMAS.map(t => (
                <button
                  key={t.id} type="button" role="menuitemradio" aria-checked={theme === t.id}
                  className={'dk-menu__item dk-menu__item--opcion' + (theme === t.id ? ' dk-menu__item--activa' : '')}
                  onClick={() => setTheme(t.id)}
                >
                  {t.label}
                  {theme === t.id && <Check size={14} strokeWidth={2} aria-hidden="true" />}
                </button>
              ))}
            </div>
          )}
          <span className="dk-menu__filete" role="separator" />
          <button type="button" role="menuitem" className="dk-menu__item" onClick={() => { setAbierto(false); onLogout() }}>
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  )
}

export default function BarraSuperior({ empresa, viewLabel, user, theme, setTheme, onLogout, onPerfil, onToggleLeft }) {
  return (
    <header className="dk-barra">
      <div className="dk-barra__izq">
        <button type="button" className="dk-barra__panel" onClick={onToggleLeft} aria-label="Alternar navegación" title="Alternar panel izquierdo">
          <PanelLeft size={18} strokeWidth={1.8} aria-hidden="true" />
        </button>
        <div className="dk-barra__miga">
          <span className="dk-barra__empresa">{empresa}{' '}<span className="dk-barra__sufijo">Pórtico</span></span>
          {viewLabel && (
            <>
              <span className="dk-barra__sep" aria-hidden="true">/</span>
              <span className="dk-barra__vista">{viewLabel}</span>
            </>
          )}
        </div>
      </div>
      <div className="dk-barra__der">
        {user && <MenuUsuario user={user} theme={theme} setTheme={setTheme} onLogout={onLogout} onPerfil={onPerfil} />}
      </div>
    </header>
  )
}
